import { useCallback, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import { isAllowed as checkAllowed } from '@helpers/auth';
import {
  logout,
  updateMerchantProfile as doUpdateMerchantProfile,
  updateRepresentant as doUpdateRepresentant,
} from '@store/login/actions';
import * as MERCHANT_STATUS from '@constants/MerchantStatus';

export const sellerTypes = {
  ON_US: 'ON US',
  SELLER: 'gsc',
  ALIANZAMIXTO: 'alianzamixto',
  ALIANZA: 'alianza',
};

const useAuth = () => {
  const dispatch = useDispatch();
  const auth = useSelector((state) => state.login);
  const { merchant, loguedAs } = useSelector((state) => state.login);
  const roles = useSelector((state) => state.login.loguedAs?.roles);
  const isSeller = useCallback(
    () => merchant?.seller?.length > 0 && merchant?.seller_id?.length > 0,
    [merchant]
  );
  const isOnus = useMemo(() => merchant.seller === sellerTypes.ON_US, [merchant]);
  const isAlianza = useCallback(() => merchant.seller === sellerTypes.ALIANZA, [merchant]);
  const isAlianzaMixto = useCallback(
    () => merchant.seller === sellerTypes.ALIANZAMIXTO,
    [merchant]
  );

  const isRL = useCallback(
    () => (loguedAs && loguedAs.is_legal_agent) || (loguedAs && loguedAs.roles[0] === 'ADMIN'),
    [loguedAs]
  );

  const doLogout = useCallback(
    () => dispatch(logout(auth.auth.payload)),
    [auth.auth?.payload, dispatch]
  );
  const isAllowed = (action) => checkAllowed(action);

  /**
   * @returns {boolean} Returns `true` if the current USER is legal AGENT. `false` otherwise.
   */
  const isLegalAgent = () => loguedAs?.is_legal_agent;

  /**
   * @returns {boolean} `true` if the current MERCHANT is a legal ENTITY. `false` otherwise.
   */
  const isLegalEntity = () => merchant.merchant_type.toUpperCase() === MERCHANT_STATUS.LEGAL_ENTITY;

  const isTCMsg = () => merchant?.tyc.accepted;

  const isVendedor = loguedAs && loguedAs.roles[0] === 'SELLER';

  const updateMerchantProfile = (legalAgentId, merchantProfile) => {
    dispatch(doUpdateMerchantProfile(legalAgentId, merchantProfile));
  };

  const widgetFundsOut = loguedAs && loguedAs.roles[0] === 'ADMIN';

  const updateRepresentant = (
    {
      legalAgentId,
      representant,
      isNewPhone = false,
      isNewEmail = false,
      password = null,
      updateUserSelected = false,
    },
    callbackError
  ) => {
    dispatch(
      doUpdateRepresentant(
        {
          legalAgentId,
          representant,
          isNewPhone,
          isNewEmail,
          password,
          updateUserSelected,
        },
        callbackError
      )
    );
  };

  const hasFalabellaHelp = useMemo(
    () => (isSeller() || isAlianzaMixto()) && !isAlianza(),
    [isSeller, isAlianzaMixto, isAlianza]
  );

  return {
    auth,
    roles,
    merchant,
    loguedAs,
    doLogout,
    isAllowed,
    isLegalAgent,
    isLegalEntity,
    isSeller,
    isOnus,
    isRL,
    isAlianza,
    isAlianzaMixto,
    updateMerchantProfile,
    updateRepresentant,
    isTCMsg,
    isVendedor,
    widgetFundsOut,
    hasFalabellaHelp,
  };
};

export default useAuth;
